// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import type { GeometriEditor } from '../../app/bygning/GeometriEditorContext';
import { DEFAULT_SECTION_DEPTH, sectionLength, type SectionLine } from '../../bygning/section';
import type { XY } from '../../bygning/planWalls';
import type { ViewportHandle } from '../../viewport/Viewport';
import styles from './GeometriLayer.module.css';
import own from './SectionLineLayer.module.css';

export interface SectionLineLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  z: number;
}

/** A cut shorter than this (metres) is a slip of the hand. */
const MIN_CUT = 0.3;

/** Shift: square to the storey's axes (the world axes when it has none). */
function squared(a: XY, b: XY, axis: number): XY {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const L = Math.hypot(dx, dy);
  const t = Math.atan2(dy, dx) - axis;
  const q = Math.round(t / (Math.PI / 2)) * (Math.PI / 2) + axis;
  return [a[0] + Math.cos(q) * L, a[1] + Math.sin(q) * L];
}

/**
 * The cut A–A on the plan while Snit is shown under it (frontend-u0o.7):
 * the line with its band and A / A′ at its ends, both ends grips; and Ny
 * snitlinje — the next two clicks on the plan set A and B (Shift squares
 * it to the building's axes, Esc gives up).
 */
export function SectionLineLayer({ viewport, host, editor, z }: SectionLineLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [first, setFirstState] = useState<XY | null>(null);
  const [pointer, setPointer] = useState<XY | null>(null);
  const [dragEnd, setDragEnd] = useState<{ end: 'a' | 'b'; at: XY } | null>(null);
  const live = useRef({ editor, z, first });
  live.current = { editor, z, first };
  const setFirst = (p: XY | null) => {
    live.current.first = p;
    setFirstState(p);
  };
  const axis = ((editor.snapTargets?.axis_angles?.[0] ?? 0) * Math.PI) / 180;

  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    bump();
    return () => {
      off();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const planAt = useCallback(
    (clientX: number, clientY: number): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(clientX - r.left, clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    },
    [viewport],
  );

  // Ny snitlinje: two clicks.
  const drawing = editor.sectionDraw;
  useEffect(() => {
    if (!drawing) setFirst(null);
  }, [drawing]);
  useEffect(() => {
    const el = host.current;
    if (!el || !drawing) return;
    const onMove = (e: PointerEvent) => {
      const p = planAt(e.clientX, e.clientY);
      const f = live.current.first;
      setPointer(p && f && e.shiftKey ? squared(f, p, axis) : p);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const raw = planAt(e.clientX, e.clientY);
      if (!raw) return;
      const f = live.current.first;
      if (!f) {
        setFirst(raw);
        return;
      }
      const b = e.shiftKey ? squared(f, raw, axis) : raw;
      if (Math.hypot(b[0] - f[0], b[1] - f[1]) < MIN_CUT) return;
      const ed = live.current.editor;
      const r6 = (v: number) => Math.round(v * 1e6) / 1e6;
      ed.setSection({ a: [r6(f[0]), r6(f[1])], b: [r6(b[0]), r6(b[1])], depth: ed.section?.depth ?? DEFAULT_SECTION_DEPTH });
      ed.setSectionDraw(false);
      setFirst(null);
    };
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    const prev = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.style.cursor = prev;
      setPointer(null);
    };
  }, [host, drawing, planAt, axis]);
  useEffect(() => {
    if (!drawing) return;
    live.current.editor.gesture.set({
      hint: { lead: first ? 'Snit · klik linjens andet punkt (B)' : 'Snit · klik linjens første punkt (A)', keys: [{ key: '⇧', text: 'ret vinkel' }, { key: 'Esc', text: 'annullér' }] },
      readout: first && pointer ? `${Math.hypot(pointer[0] - first[0], pointer[1] - first[1]).toFixed(2).replace('.', ',')} m` : null,
    });
    return () => live.current.editor.gesture.set(null);
  }, [drawing, first, pointer]);

  // An end's grip: drag it.
  const startEnd = (end: 'a' | 'b', e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const line0 = live.current.editor.section;
    if (!line0) return;
    const other = end === 'a' ? line0.b : line0.a;
    const move = (m: PointerEvent) => {
      const p = planAt(m.clientX, m.clientY);
      if (p) setDragEnd({ end, at: m.shiftKey ? squared(other, p, axis) : p });
    };
    const up = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      setDragEnd((d) => {
        const ed = live.current.editor;
        const cur = ed.section;
        if (d && cur && Math.hypot(d.at[0] - other[0], d.at[1] - other[1]) >= MIN_CUT) ed.setSection({ ...cur, [d.end]: d.at } as SectionLine);
        return null;
      });
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
  };

  const line = editor.section;
  const shown: SectionLine | null = line && dragEnd ? { ...line, [dragEnd.end]: dragEnd.at } : line;
  const drawn: [XY, XY] | null = drawing && first && pointer ? [first, pointer] : null;
  const seg = drawn ?? (shown && !drawing ? ([shown.a, shown.b] as [XY, XY]) : null);
  const A = seg ? toScreen(seg[0]) : null;
  const B = seg ? toScreen(seg[1]) : null;
  // The band the section shows the cloud of, as a quad on the plan.
  let band: string | null = null;
  if (shown && !drawing && A && B && sectionLength(shown) > 1e-6) {
    const L = sectionLength(shown);
    const h = shown.depth / 2;
    const n: XY = [(-(shown.b[1] - shown.a[1]) / L) * h, ((shown.b[0] - shown.a[0]) / L) * h];
    const corners: XY[] = [
      [shown.a[0] + n[0], shown.a[1] + n[1]],
      [shown.b[0] + n[0], shown.b[1] + n[1]],
      [shown.b[0] - n[0], shown.b[1] - n[1]],
      [shown.a[0] - n[0], shown.a[1] - n[1]],
    ];
    const q = corners.map((p) => toScreen(p));
    if (q.every(Boolean)) band = q.map((p) => `${p![0].toFixed(1)},${p![1].toFixed(1)}`).join(' ');
  }
  // The looking direction (the section is seen from the right of A → B, looking left): a tick at each end.
  const arrow = (at: [number, number], a: [number, number], b: [number, number]) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const L = Math.hypot(dx, dy) || 1;
    const nx = dy / L;
    const ny = -dx / L;
    const tip = [at[0] + nx * 16, at[1] + ny * 16];
    return `M${at[0].toFixed(1)},${at[1].toFixed(1)} L${tip[0].toFixed(1)},${tip[1].toFixed(1)} M${(tip[0] - nx * 6 + ny * 4).toFixed(1)},${(tip[1] - ny * 6 - nx * 4).toFixed(1)} L${tip[0].toFixed(1)},${tip[1].toFixed(1)} L${(tip[0] - nx * 6 - ny * 4).toFixed(1)},${(tip[1] - ny * 6 + nx * 4).toFixed(1)}`;
  };

  return (
    <div ref={layerRef} className={styles.layer}>
      <svg className={styles.svg} aria-hidden="true">
        {band && <polygon points={band} className={own.band} />}
        {A && B && <line x1={A[0]} y1={A[1]} x2={B[0]} y2={B[1]} className={own.cut} />}
        {A && B && !drawn && (
          <>
            <path d={arrow(A, A, B)} className={own.look} />
            <path d={arrow(B, A, B)} className={own.look} />
          </>
        )}
        {drawing && first && (() => {
          const s = toScreen(first);
          return s ? <circle cx={s[0]} cy={s[1]} r={4} className={styles.chainPoint} /> : null;
        })()}
        {!drawing && A && B && line && (
          <>
            <circle cx={A[0]} cy={A[1]} r={6} className={styles.grip} onPointerDown={(e) => startEnd('a', e)}>
              <title>Træk snittets ende A</title>
            </circle>
            <circle cx={B[0]} cy={B[1]} r={6} className={styles.grip} onPointerDown={(e) => startEnd('b', e)}>
              <title>Træk snittets ende A′</title>
            </circle>
          </>
        )}
      </svg>
      {A && !drawn && (
        <span className={own.mark} style={{ transform: `translate(${(A[0] - 18).toFixed(1)}px, ${(A[1] - 22).toFixed(1)}px)` }}>
          A
        </span>
      )}
      {B && !drawn && (
        <span className={own.mark} style={{ transform: `translate(${(B[0] + 8).toFixed(1)}px, ${(B[1] - 22).toFixed(1)}px)` }}>
          A′
        </span>
      )}
    </div>
  );
}
